import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import ShareIcon from "@mui/icons-material/Share";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useDashboardData } from "../../hooks/useDashboardData";
import CardFilter from "../../components/dashboard/CardFilter";
import { useAppSelector } from "../../hooks/useAuth";
import CustomLoading from "../../components/common/CustomLoading";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const KPI_CARD_HEIGHT = 72;

const TopKpiCard = ({
  title,
  value,
  bg,
  icon,
}: {
  title: string;
  value: React.ReactNode;
  bg: string;
  icon?: React.ReactNode;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: KPI_CARD_HEIGHT,
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        background: bg,
        color: "#fff",
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            opacity: 0.95,
            mb: 0.5,
            fontSize: "0.875rem",
            lineHeight: 1.3,
            /* Same vertical slot for 1- or 2-line titles so row cards stay equal height */
            minHeight: "2.6em",
          }}
        >
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>

      <Avatar
        variant="rounded"
        sx={{
          bgcolor: "rgba(255,255,255,0.2)",
          width: 48,
          height: 48,
          ml: 2,
        }}
      >
        {icon}
      </Avatar>
    </Paper>
  );
};

const DashboardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.roleid === 1;

  const getHubIdForUser = () => {
    if (user?.roleid === 1) {
      return 0;
    }
    const hubId = localStorage.getItem('motovolt_hub_id');
    return hubId ? Number.parseInt(hubId, 10) : 0;
  };

  const hubId = getHubIdForUser();
  const { data, loading, error, updateFilters, refetch } = useDashboardData(hubId);
  
  const [revenueFilter, setRevenueFilter] = React.useState<'daily' | 'weekly'>('weekly');
  const [rideFilter, setRideFilter] = React.useState<'daily' | 'weekly'>('weekly');
  const [customerFilter, setCustomerFilter] = React.useState<'daily' | 'weekly'>('weekly');

  React.useEffect(() => {
    const state = location.state as any;
    if (state?.refresh || state?.refreshHubs) {
      refetch();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, refetch, navigate, location.pathname]);

  React.useEffect(() => {
    updateFilters({
      revenue: revenueFilter,
      ride: rideFilter,
      customer: customerFilter,
    });
  }, [revenueFilter, rideFilter, customerFilter, updateFilters]);

  if (!data) {
    if (loading) {
      return <CustomLoading message="Loading Dashboard..." />;
    }
    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Typography variant="h6" color="text.secondary">
              Failed to load dashboard data. Please try again.
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  }

  const dashboardData = data;

  const extractChartData = (dataObj: Record<string, string> | string[] | null | undefined) => {
    if (!dataObj) return { labels: [], values: [] };
    
    if (Array.isArray(dataObj)) {
      const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const labels = weekDays.slice(0, dataObj.length);
      const values = dataObj.map(val => Number.parseFloat(val));
      return { labels, values };
    }
    
    if (typeof dataObj === 'object') {
      const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];      
      const keys = Object.keys(dataObj);
      const isDateFormat = keys.some(key => key.includes(' '));
      
      let sortedKeys: string[];
      
      if (isDateFormat) {
        sortedKeys = [...keys].sort((a, b) => {
          const dayA = Number.parseInt(a.split(' ')[0]);
          const dayB = Number.parseInt(b.split(' ')[0]);
          return dayA - dayB;
        });
      } else {
        sortedKeys = [...keys].sort((a, b) => {
          const indexA = dayOrder.indexOf(a);
          const indexB = dayOrder.indexOf(b);
          
          if (indexA === -1 || indexB === -1) {
            return a.localeCompare(b);
          }
          return indexA - indexB;
        });
      }
      
      const labels = sortedKeys;
      const values = sortedKeys.map(key => Number.parseFloat(dataObj[key]));
      return { labels, values };
    }
    
    return { labels: [], values: [] };
  };

  const ensureObject = (value: any): Record<string, string> => {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    return {};
  };

  const calculateAxisConfig = (values: number[], defaultValue: number = 100) => {
    if (values.length === 0) return { max: defaultValue, stepSize: defaultValue / 4 };
    
    const dataMax = Math.max(...values);
    if (dataMax === 0) return { max: defaultValue, stepSize: defaultValue / 4 };
    
    const paddedMax = dataMax * 1.2;
    const roundedMax = Math.ceil(paddedMax / 10) * 10;
    
    const stepSize = Math.ceil(roundedMax / 5 / 10) * 10;
    
    return { max: roundedMax, stepSize };
  };

  const revenueChartData = extractChartData(dashboardData.revenuegrowth);
  const revenueAxisConfig = calculateAxisConfig(revenueChartData.values);
  
  const revenueLineData = {
    labels: revenueChartData.labels,
    datasets: [
      {
        label: "Revenue",
        data: revenueChartData.values,
        fill: false,
        tension: 0.35,
        pointRadius: 5,
        borderWidth: 3,
        borderColor: "#673AB7",
        backgroundColor: "#673AB7",
      },
    ],
  };

  const revenueLineOptions: any = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function(context: any) {
            return `Revenue: ${context.parsed.y.toFixed(2)}K`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#6b7280" },
      },
      y: {
        grid: { color: "#f2f4f7" },
        ticks: { 
          color: "#6b7280", 
          beginAtZero: true,
          callback: function(value: any) {
            return value.toFixed(0) + 'K';
          }
        },
        max: revenueAxisConfig.max,
        stepSize: revenueAxisConfig.stepSize
      },
    },
  };

  const rideChartData = extractChartData(dashboardData.ridevolume);
  const rideAxisConfig = calculateAxisConfig(rideChartData.values);
  
  const rideBarData = {
    labels: rideChartData.labels,
    datasets: [
      {
        label: "Rides",
        data: rideChartData.values,
        backgroundColor: "#4CAF50",
        borderRadius: 6,
      },
    ],
  };
  const rideBarOptions: any = {
    maintainAspectRatio: false,
    plugins: { 
      legend: { display: false }, 
      tooltip: { 
        enabled: true,
        callbacks: {
          label: function(context: any) {
            return `Rides Per Day: ${context.parsed.y}`;
          }
        }
      } 
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#6b7280" },
      },
      y: {
        grid: { color: "#f2f4f7" },
        ticks: { 
          color: "#6b7280", 
          beginAtZero: true,
          stepSize: rideAxisConfig.stepSize,
          max: rideAxisConfig.max
        },
      },
    },
  };

  const donutData = {
    labels: ["Available", "In Use", "Maintenance"],
    datasets: [
      {
        data: [
          dashboardData.vehicle_available,
          dashboardData.vehicle_in_use,
          dashboardData.vehicle_maintenance,
        ],
        backgroundColor: ["#2f7cf6", "#27c9a9", "#ffb020"],
        hoverOffset: 6,
      },
    ],
  };
  const donutOptions: any = {
    maintainAspectRatio: false,
    plugins: { 
      legend: { 
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return `${context.label}: ${context.parsed}`;
          }
        }
      }
    },
    cutout: "70%",
  };

  const customerChartData = extractChartData(dashboardData.customergrowth);
  const customerAxisConfig = calculateAxisConfig(customerChartData.values);
  
  const areaData = {
    labels: customerChartData.labels,
    datasets: [
      {
        label: "Customers",
        data: customerChartData.values,
        fill: true,
        backgroundColor: "rgba(99,102,241,0.12)",
        borderColor: "rgba(99,102,241,1)",
        tension: 0.4,
        pointRadius: 4,
      },
    ],
  };
  const areaOptions: any = {
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { 
        grid: { display: false }, 
        ticks: { color: "#6b7280" } 
      },
      y: { 
        grid: { color: "#f2f4f7" }, 
        ticks: { 
          color: "#6b7280",
          stepSize: customerAxisConfig.stepSize,
          max: customerAxisConfig.max
        } 
      },
    },
  };

  const hubPerformanceValues = Object.values(ensureObject(dashboardData.hubperformance)).map(val => Number.parseFloat(val));
  const hubAxisConfig = calculateAxisConfig(hubPerformanceValues);
  
  const hubPerformanceData = {
    labels: Object.keys(ensureObject(dashboardData.hubperformance)),
    datasets: [
      {
        label: "Hub Performance",
        data: hubPerformanceValues,
        backgroundColor: "#4CAF50",
        borderRadius: 4,
      },
    ],
  };

  const hubOptions: any = {
    maintainAspectRatio: false,
    plugins: { 
      legend: { display: false }, 
      tooltip: { 
        enabled: true,
        callbacks: {
          label: function(context: any) {
            return `${context.label} Completed Rides: ${context.parsed.y}`;
          }
        }
      } 
    },
    scales: {
      x: { 
        grid: { display: false }, 
        ticks: { color: "#6b7280" },
        categoryPercentage: 0.5,
        barPercentage: 0.8,
      },
      y: { 
        grid: { color: "#f2f4f7" }, 
        ticks: { 
          color: "#6b7280", 
          beginAtZero: true,
          stepSize: hubAxisConfig.stepSize,
          max: hubAxisConfig.max
        } 
      },
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3, width: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            alignItems: "stretch",
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Total Vehicles"
              value={dashboardData.totalvehicles.toString()}
              icon={<DirectionsCarIcon />}
              bg="#3F51B5"
            />
          </Box>
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Active Rides"
              value={dashboardData.activerides.toString()}
              icon={<TrendingUpIcon />}
              bg="#4CAF50"
            />
          </Box>
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Revenue Today"
              value={`₹${dashboardData.revenuetoday.toLocaleString()}`}
              icon={<AccountBalanceWalletIcon />}
              bg="#2196F3"
            />
          </Box>
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Total Users"
              value={dashboardData.totalusers.toLocaleString()}
              icon={<PeopleIcon />}
              bg="#FF9800"
            />
          </Box>
          {isSuperAdmin ? (
            <Box sx={{ minWidth: 0, display: "flex" }}>
              <TopKpiCard
                title="Total Hubs"
                value={dashboardData.totalhubs.toString()}
                icon={<HomeWorkIcon />}
                bg="#673AB7"
              />
            </Box>
          ) : (
            <Box sx={{ minWidth: 0, display: "flex" }}>
              <TopKpiCard
                title="Total Revenue by Admin"
                value={`₹${Number(dashboardData.todaybookingrevenuebymanager ?? 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}`}
                icon={<MonetizationOnIcon />}
                bg="#009688"
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isSuperAdmin
              ? "repeat(5, minmax(0, 1fr))"
              : "repeat(5, minmax(0, 1fr))",
            alignItems: "stretch",
            gap: 2,
            width: "100%",
          }}
        >
          {isSuperAdmin && (
            <Box sx={{ minWidth: 0, display: "flex" }}>
              <TopKpiCard
                title="Total Revenue by Admin"
                value={`₹${Number(dashboardData.todaybookingrevenuebymanager ?? 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}`}
                icon={<MonetizationOnIcon />}
                bg="#009688"
              />
            </Box>
          )}
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Total Bookings by Admin"
              value={Number(dashboardData.todaybookingcountbymanager ?? 0).toLocaleString("en-IN")}
              icon={<EventNoteIcon />}
              bg="#5C6BC0"
            />
          </Box>
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Total Promo Codes Used"
              value={Number(dashboardData.total_promocodes_used_count ?? 0).toLocaleString("en-IN")}
              icon={<LocalOfferIcon />}
              bg="#7E57C2"
            />
          </Box>
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Total Discount (Promo Code)"
              value={`₹${Number(dashboardData.total_discount_using_prmocode ?? 0).toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}`}
              icon={<PriceChangeIcon />}
              bg="#00897B"
            />
          </Box>
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Total Referral Codes Used"
              value={Number(dashboardData.total_referralcodes_used_count ?? 0).toLocaleString("en-IN")}
              icon={<ShareIcon />}
              bg="#3949AB"
            />
          </Box>
          <Box sx={{ minWidth: 0, display: "flex" }}>
            <TopKpiCard
              title="Total Discount (Referrals)"
              value={`₹${Number(dashboardData.total_discount_using_referrals ?? 0).toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}`}
              icon={<MoneyOffIcon />}
              bg="#6D4C41"
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                  Revenue Growth Trend
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Performance overview
                </Typography>
              </Box>
              <CardFilter 
                value={revenueFilter} 
                onChange={setRevenueFilter} 
                loading={loading} 
              />
            </Box>
            <Box sx={{ height: 200 }}>
              <Line data={revenueLineData} options={revenueLineOptions} />
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                  Ride Volume Trend
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Trips completed overview
                </Typography>
              </Box>
              <CardFilter 
                value={rideFilter} 
                onChange={setRideFilter} 
                loading={loading} 
              />
            </Box>
            <Box sx={{ height: 200 }}>
              <Bar data={rideBarData} options={rideBarOptions} />
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                  Vehicle Utilization
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Fleet status distribution
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={3}>
              <Box sx={{ width: 200, height: 200 }}>
                <Doughnut data={donutData} options={donutOptions} />
              </Box>
              <Box sx={{ flex: 1 }}>
                {[
                  { label: "Available", value: dashboardData.vehicle_available.toString(), color: "#2196F3" },
                  { label: "In Use", value: dashboardData.vehicle_in_use.toString(), color: "#4CAF50" },
                  { label: "Maintenance", value: dashboardData.vehicle_maintenance.toString(), color: "#FF9800" },
                ].map((s) => (
                  <Box display="flex" alignItems="center" justifyContent="space-between" key={s.label} sx={{ mb: 1.5 }}>
                    <Box display="flex" alignItems="center">
                      <Box sx={{ width: 12, height: 12, bgcolor: s.color, borderRadius: "2px", mr: 1.5 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>{s.label}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>{s.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                  Customer Growth Trend
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Customer growth overview
                </Typography>
              </Box>
              <CardFilter 
                value={customerFilter} 
                onChange={setCustomerFilter} 
                loading={loading} 
              />
            </Box>
            <Box sx={{ height: 200 }}>
              <Line data={areaData} options={areaOptions} />
            </Box>
          </Paper>
        </Box>
      </Box>

      {isSuperAdmin && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                Hub Performance
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Rides this week
              </Typography>
            </Box>
          </Box>
          <Box sx={{ height: 250  , width: '20%'}}>
            <Bar data={hubPerformanceData} options={hubOptions} />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default DashboardPage;
